import React from 'react';
import { Sparkles, Users, MapPin } from 'lucide-react';

/**
 * Секция формы с налоговыми вычетами
 */
const DeductionsSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  russianRegions
}) => {
  // Если нет вычетов, не показываем полную секцию
  if (!formData.hasDeductions && !formData.includeSocialBenefits) return (
    <div className="mb-6 bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
        Вычеты и льготы
      </h3>

      <div className="grid grid-cols-1 gap-4">
        {/* Чекбокс для включения вычетов */}
        <div>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="hasDeductions"
              checked={formData.hasDeductions}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать налоговые вычеты
            </span>
          </label>
        </div>

        {/* Чекбокс для социальных льгот */}
        <div>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="includeSocialBenefits"
              checked={formData.includeSocialBenefits}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать региональные льготы
            </span>
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mb-6 bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
        Вычеты и льготы
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Чекбокс для включения вычетов */}
        <div className="col-span-full">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="hasDeductions"
              checked={formData.hasDeductions}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать налоговые вычеты
            </span>
          </label>
        </div>

        {formData.hasDeductions && (
          <>
            {/* Сумма вычетов */}
            <div>
              <label htmlFor="deductions" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Сумма вычетов
              </label>
              <input
                type="text"
                id="deductions"
                name="deductions"
                value={formData.deductions}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.deductions ? 'input-error' : ''}`}
                placeholder="Введите сумму вычетов"
              />
              {errors.deductions && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.deductions}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Для НПД налоговый бонус применяется автоматически
              </p>
            </div>

            {/* Вычет на детей */}
            <div>
              <div className="mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="hasChildrenDeduction"
                    checked={formData.hasChildrenDeduction}
                    onChange={handleInputChange}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                    <Users className="w-4 h-4 mr-1 text-purple-500" />
                    Вычет на детей
                  </span>
                </label>
              </div>
              
              {formData.hasChildrenDeduction && (
                <div>
                  <label htmlFor="childrenCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Количество детей
                  </label>
                  <input
                    type="number"
                    id="childrenCount"
                    name="childrenCount"
                    value={formData.childrenCount}
                    onChange={handleInputChange}
                    min="0"
                    max="10"
                    className="form-input w-full"
                  />
                </div>
              )}
            </div>
          </>
        )}

        {/* Чекбокс для социальных льгот */}
        <div className="col-span-full mt-2">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="includeSocialBenefits"
              checked={formData.includeSocialBenefits}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать региональные льготы
            </span>
          </label>
        </div>

        {formData.includeSocialBenefits && (
          <div className="col-span-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Выбор региона */}
              <div>
                <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <MapPin className="w-4 h-4 mr-1 text-purple-500" />
                  Регион
                </label>
                <select
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  className="form-select w-full"
                >
                  {russianRegions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Информация о региональных льготах */}
              <div className="md:flex md:items-end">
                <div className="bg-white dark:bg-gray-800 p-2 rounded-md border border-gray-200 dark:border-gray-700 text-xs w-full">
                  <p className="font-medium mb-1">Льготы в выбранном регионе:</p>
                  {formData.region === 'Moscow' && (
                    <p className="text-gray-600 dark:text-gray-400">
                      • Налоговые каникулы для впервые зарегистрированных ИП на УСН<br />
                      • Пониженная ставка по УСН "Доходы" для IT-компаний - 1%
                    </p>
                  )}
                  {formData.region === 'SaintPetersburg' && (
                    <p className="text-gray-600 dark:text-gray-400">
                      • Ставка 3% по УСН "Доходы" для определенных видов деятельности<br />
                      • Налоговые каникулы для впервые зарегистрированных ИП
                    </p>
                  )}
                  {formData.region !== 'Moscow' && formData.region !== 'SaintPetersburg' && (
                    <p className="text-gray-600 dark:text-gray-400 italic">
                      Для расчета с учетом региональных льгот обратитесь в налоговую службу вашего региона
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Информация о вычетах для НПД */}
        {formData.taxSystem === 'npd' && (
          <div className="col-span-full bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700 text-sm mt-2">
            <div className="font-medium mb-1">Налоговый вычет для самозанятых:</div>
            <ul className="text-gray-600 dark:text-gray-400 text-xs space-y-1">
              <li>• Налоговый бонус до 10 000 ₽ (учитывается автоматически)</li>
              <li>• Уменьшает ставку 4% до 3% и ставку 6% до 4% до исчерпания вычета</li>
              <li>• Налоговый вычет предоставляется автоматически, вам не нужно его запрашивать</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeductionsSection;